import type { ReactNode } from 'react'
import { BigLeafRank } from '../../../../components/know-why/score-sections/BigLeafRank'
import { StripContent } from '../../../../components/know-why/score-sections/StripContent'
import { getTagFromPowerForm } from '../../../../components/know-why/utils'
import { NumberedItem } from '../../../../components/ui-components/NumberedItem'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { EliteScoreTrends, PowerPerformanceForms } from '../../../../types/mutual-fund-detail.types'
import { mapRatingToPowerForm } from '../utils/mapRatingToPowerFormUtils'
import {
  PlanRecommendationReasonHighlightStrip,
  resolveEliteScoreTrendFromPerformanceTag
} from './PlanRecommendationReasonHighlightStrip'

type PlanRecommendationScoreReasonPointerProps = {
  index: number
  borderBottom?: boolean
}

export function PowerRankPointer({
  index,
  borderBottom,
  categoryLabel,
  powerRank,
  powerRating,
  totalCategoryCount
}: PlanRecommendationScoreReasonPointerProps & {
  categoryLabel: string
  powerRank: number | null
  powerRating: string
  totalCategoryCount: number | null
}) {
  const powerForm = mapRatingToPowerForm(powerRating) ?? PowerPerformanceForms.IN_FORM
  const tag = getTagFromPowerForm(powerForm)

  return (
    <NumberedItem
      number={index}
      title="Power Rank"
      subtitle={`in ${categoryLabel}`}
      borderBottom={borderBottom}
    >
      <PlanRecommendationReasonHighlightStrip tag={tag}>
        <StripContent
          tag={tag}
          isElite
          customContent={(
            <BigLeafRank
              isElite
              powerRank={powerRank}
              categoryCount={totalCategoryCount}
              powerForm={powerForm}
            />
          )}
          description="Fund's overall rank vs others in same category"
          descriptionClassName="pt-1"
        />
      </PlanRecommendationReasonHighlightStrip>
    </NumberedItem>
  )
}

export function ConsistencyScorePointer({
  index,
  borderBottom,
  categoryLabel,
  score,
  maxScore,
  performanceTag
}: PlanRecommendationScoreReasonPointerProps & {
  categoryLabel: string
  score: number | null | undefined
  maxScore: number
  performanceTag: string
}) {
  if (score == null) {
    return null
  }

  const tag = resolveEliteScoreTrendFromPerformanceTag(performanceTag)

  return (
    <NumberedItem
      number={index}
      title="Consistency Score"
      subtitle={`in ${categoryLabel}`}
      borderBottom={borderBottom}
    >
      <PlanRecommendationReasonHighlightStrip tag={tag}>
        <StripContent
          tag={tag}
          isElite
          score={score}
          maxScore={maxScore}
          description="Fund's consistency in giving high returns vs others in same category"
          descriptionClassName="pt-1"
        />
      </PlanRecommendationReasonHighlightStrip>
    </NumberedItem>
  )
}

export function RecencyScorePointer({
  index,
  borderBottom,
  categoryLabel,
  score,
  maxScore,
  performanceTag
}: PlanRecommendationScoreReasonPointerProps & {
  categoryLabel: string
  score: number | null | undefined
  maxScore: number
  performanceTag: string
}) {
  if (score == null) {
    return null
  }

  const tag = resolveEliteScoreTrendFromPerformanceTag(performanceTag)

  return (
    <NumberedItem
      number={index}
      title="Recency Score"
      subtitle={`in ${categoryLabel}`}
      borderBottom={borderBottom}
    >
      <PlanRecommendationReasonHighlightStrip tag={tag}>
        <StripContent
          tag={tag}
          isElite
          score={score}
          maxScore={maxScore}
          description="Fund's recent performance over last 2 years vs others in same category"
          descriptionClassName="pt-1"
        />
      </PlanRecommendationReasonHighlightStrip>
    </NumberedItem>
  )
}

export function VolatilityScorePointer({
  index,
  borderBottom,
  categoryLabel,
  score,
  maxScore,
  performanceTag
}: PlanRecommendationScoreReasonPointerProps & {
  categoryLabel: string
  score: number | null | undefined
  maxScore: number
  performanceTag: string
}) {
  if (score == null) {
    return null
  }

  const tag = resolveEliteScoreTrendFromPerformanceTag(performanceTag)

  return (
    <NumberedItem
      number={index}
      title="Volatility Score"
      subtitle={`in ${categoryLabel}`}
      borderBottom={borderBottom}
    >
      <PlanRecommendationReasonHighlightStrip tag={tag}>
        <StripContent
          tag={tag}
          isElite
          score={score}
          maxScore={maxScore}
          description="Fund's stability during market swings vs others in same category"
          descriptionClassName="pt-1"
        />
      </PlanRecommendationReasonHighlightStrip>
    </NumberedItem>
  )
}

export function PlanRecommendationReasonTextStrip({
  highlightTitle,
  highlightDescription,
  tag = EliteScoreTrends.GOOD
}: {
  highlightTitle: string
  highlightDescription: string
  tag?: EliteScoreTrends
}) {
  return (
    <PlanRecommendationReasonHighlightStrip tag={tag}>
      <div className="flex flex-col gap-1">
        <div className="text-[18px] font-[500] leading-[150%]">{highlightTitle}</div>
        <RenderHTML
          className="text-[13px] font-[400] leading-[140%]"
          innerHTMLText={highlightDescription}
        />
      </div>
    </PlanRecommendationReasonHighlightStrip>
  )
}

export function PlanRecommendationReasonScoreStrip({
  tag,
  children
}: {
  tag: EliteScoreTrends
  children: ReactNode
}) {
  return <PlanRecommendationReasonHighlightStrip tag={tag}>{children}</PlanRecommendationReasonHighlightStrip>
}
